import { computed, toValue, watch, watchEffect } from "vue";
import type { InputConfig } from "./useForm"
import useWooCart from "./useWooCart";
import useItlpApi from "./useItlpApi";
import type { WoocommerceBillingAddress } from "@/types/woocommerce.types";
import useGlobalAlert from "./useGlobalAlert";
import useForm from "./useForm";
import useCheckout from "./useCheckout";

const billingFields = [
	{
		key: 'firstname',
		checkout_field: 'first_name',
	},
	{
		key: 'lastname',
		checkout_field: 'last_name'
	},
	{
		key: 'address',
		checkout_field: 'address_1'
	},
	{
		key: 'city'
	},
	{
		key: 'province',
		checkout_field: 'state'
	},
	{
		key: 'postcode'
	},
	{
		key: 'country',
		default: 'IT'
	},
	{
		key: 'birthday',
		checkout_field: 'birth_day',
		checkout_api_field: 'itlp/birth_day'
	},
	{
		key: 'birthplace',
		checkout_field: 'birth_place',
		checkout_api_field: 'itlp/birth_place'
	},
	{
		key: 'phone'
	},
	{
		key: 'fiscalcode',
		checkout_api_field: 'itlp/fiscalcode'
	},
	{
		key: 'account_type',
		checkout_api_field: 'itlp/account_type'
	},
	{
		key: 'document_type',
		checkout_field: 'identity_document_type',
		checkout_api_field: 'itlp/identity_document_type'
	},
	{
		key: 'document_code',
		checkout_field: 'identity_document_number',
		checkout_api_field: 'itlp/identity_document_number'
	},
	{
		key: 'document_expiration_date',
		checkout_field: 'identity_document_expire_at',
		checkout_api_field: 'itlp/identity_document_expire_at'
	},
	{
		key: 'company'
	},
	{
		key: 'sdi',
		checkout_api_field: 'itlp/sdi'
	},
	{
		key: 'pec',
		checkout_api_field: 'itlp/pec'
	},
	{
		key: 'vat_id',
		checkout_api_field: 'itlp/vat_id'
	},
	{
		key: 'date_of_establishment',
		checkout_api_field: 'itlp/date_of_establishment'
	},
	{
		key: 'registered_office',
		checkout_api_field: 'itlp/registered_office'
	},
	{
		key: 'company_registration_number',
		checkout_api_field: 'itlp/company_registration_number'
	}
];

function getControllerKeyFromCheckoutFieldKey(fieldKey: string){
	const field = billingFields.find(f => (f.checkout_field || f.key) == fieldKey);
	return field ? field.key : null;
}


export default function (){
	const {checkoutFields, cart, updateCustomerBodyError} = useWooCart();
	const {countries, countryLocale, countryStates} = useItlpApi();
	const localeDefault = countryLocale.value['default'] || {};
	const {addAlert} = useGlobalAlert();

	function localizeProvince(country: string|null|undefined, currentConfig: InputConfig): Partial<InputConfig>{
		const locale = country ? Object.assign({}, localeDefault, countryLocale.value[country] || {}) : localeDefault;
		const newConfig: Partial<InputConfig> = {
			label: locale?.state?.label || currentConfig.label,
			type: locale?.state?.hidden ? 'hidden' : 'select',
			required: typeof(locale?.state?.required) == 'boolean' ? locale?.state?.required : true,
			options: country ? countryStates.value[country] : {}
		};

		if(newConfig.type == 'select' && (typeof(newConfig.options) != 'object' || !Object.keys(newConfig.options).length)){
			newConfig.type = 'text';
		}

		return newConfig;
	}

	function localizePostcode(country: string|null|undefined, currentConfig: InputConfig): Partial<InputConfig>{
		const locale = country ? Object.assign({}, localeDefault, countryLocale.value[country] || {}) : localeDefault;
		return {
			label: locale?.postcode?.label || currentConfig.label,
			type: locale?.postcode?.hidden ? 'hidden' : 'text',
			required: typeof(locale?.postcode?.required) == 'boolean' ? locale?.postcode?.required : true
		};
	}

	const billingInputOptions = computed(() => {
		const defaultCountry = ((cart.value?.billing_address['country']) || (cart.value?.billing_address['country']) || checkoutFields.value?.billing['billing_country'].default || 'IT') as string|null|undefined;

		return billingFields.reduce((carry, field) => {
			const billingFieldKey = 'billing_'+(field.checkout_field || field.key);
			const checkoutField = checkoutFields.value?.billing[billingFieldKey];
			if(!checkoutField){
				console.warn('not found', billingFieldKey, checkoutFields, field);
				return carry;
			}

			const initValue = (cart.value?.billing_address[(field.checkout_api_field || field.checkout_field || field.key) as keyof WoocommerceBillingAddress]) as string|null|undefined;

			const inputOptions: InputConfig = {
				key: field.key,
				apiFormKey: field.checkout_api_field || field.checkout_field,
				label: checkoutField.label,
				type: checkoutField.type,
				options: checkoutField.options,
				placeholder: checkoutField.placeholder,
				required: checkoutField.required,
				default: initValue || checkoutField.default || field.default,
				depends_on_field_key: checkoutField.custom_attributes?.['data-itlp-dependency-input-name'],
				depends_on_field_value: checkoutField.custom_attributes?.['data-itlp-dependency-input-value']
			};

			if(inputOptions.key == 'country'){
				inputOptions.type = 'select';
				inputOptions.options = toValue(countries);
			}

			if(inputOptions.key == 'province'){
				Object.assign(inputOptions, localizeProvince(defaultCountry, inputOptions))
			}

			if(inputOptions.key == 'postcode'){
				Object.assign(inputOptions, localizePostcode(defaultCountry, inputOptions))
			}

			carry.push(inputOptions);

			return carry;
		}, [] as InputConfig[]);
	});

	const form = useForm();
	const {build: buildFrom} = form;

	watch(() => toValue(billingInputOptions), (inputOptions) => {
		buildFrom({
			inputs: inputOptions
		});
	}, {immediate: true});

	const selectedCountry = computed(() => {
		return form.controllers.value['country'] && form.controllers.value['country'].value;
	});

	watch(toValue(selectedCountry), (selectedCountryValue) => {
		const _selectedCountry = toValue(selectedCountryValue) as string;
		const provinceConfig = form.controllers.value['province'].getConfig();
		const postcodeConfig = form.controllers.value['postcode'].getConfig();
		form.controllers.value['province'].value.value = null;
		form.controllers.value['province'].setConfig(Object.assign({}, provinceConfig, localizeProvince(_selectedCountry, provinceConfig)));
		form.controllers.value['postcode'].setConfig(Object.assign({}, postcodeConfig, localizePostcode(_selectedCountry, postcodeConfig)));
	});

	watch(() => toValue(updateCustomerBodyError), () => {
		const formErrors: Record<string, string> = {};
		if(updateCustomerBodyError.value && typeof(updateCustomerBodyError.value) == 'object'){
			const erroCode = updateCustomerBodyError.value.code;
			const billingAddressErrorCode = updateCustomerBodyError.value.data?.details?.billing_address?.code;
			let fieldKeyError = updateCustomerBodyError.value.data?.details?.billing_address?.data?.key;
			if(erroCode == 'rest_invalid_param' && billingAddressErrorCode == 'invalid_state'){
				formErrors['province'] = updateCustomerBodyError.value.data?.details?.billing_address?.message;
			}else if(erroCode == 'rest_invalid_param' && billingAddressErrorCode == 'invalid_postcode'){
				formErrors['postcode'] = updateCustomerBodyError.value.data?.details?.billing_address?.message;
			}else if(erroCode == 'rest_invalid_param' && typeof(fieldKeyError) === 'string'){
				const controllerKey = getControllerKeyFromCheckoutFieldKey(fieldKeyError.replace('itlp/', ''));
				if(controllerKey) formErrors[controllerKey] = updateCustomerBodyError.value.data?.details?.billing_address?.message;
			}
		}

		form.errors.value = formErrors;

		if(updateCustomerBodyError.value?.data?.params?.billing_address) addAlert(updateCustomerBodyError.value?.data?.params?.billing_address, 5000);
	});

	/**
	 * disabled edit billing data if customer just exists in itlp
	 */
	const canEdit = computed(() => {
		if(typeof(cart.value?.extensions.itlp?.can_edit_billing_address) == 'boolean') return cart.value?.extensions.itlp?.can_edit_billing_address;
		if(!cart.value?.extensions.itlp?.itlp_customer?.customer_type) return true; // deprecated
		return false;
	});

	watchEffect(() => {
		form.disabled.value = !canEdit.value;
	});

	return {
		billingInputOptions,
		form,
		canEdit
	};
}